import React, {useEffect, useState} from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {requestControl} from '@/shared/api';
import {fetchPersonnelList} from '@/entities/personnel/api';
import {createTelegramInvite} from '@/entities/telegram/api';
import type {CreateInviteResult, TelegramScopeType} from '@/entities/telegram/types';
import {shareInvite} from '@/features/manage-telegram-recipients/lib/shareInvite';
import {AppButton} from '@/shared/ui/button';
import {Banner} from '@/shared/ui/banner';
import {Card} from '@/shared/ui/card';
import {Icon} from '@/shared/ui/icons';
import {FormCard} from '@/shared/ui/form';
import {AppInput} from '@/shared/ui/input';
import {Segmented} from '@/shared/ui/segmented';
import {Skeleton} from '@/shared/ui/skeleton';
import {AppText} from '@/shared/ui/text';
import {Toggle} from '@/shared/ui/toggle';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Mint one invite link.
 *
 * The department picker deliberately reads sv_control's own department list
 * (/control/departments) rather than the organization list the rest of the app
 * uses: sv_control matches a department subscription against the person's
 * CONTROL department ids, so an organization id would bind fine and then never
 * deliver a message. Renders inside a `Screen` the caller already owns
 * (Task 6) — no `Screen` here.
 */
type ControlDepartment = {department_id: string; name: string};
type ControlDepartments = {departments: ControlDepartment[]};

const SEARCH_DEBOUNCE_MS = 300;

// Same debounce as PeopleList's person search: settle a fast-changing value so a
// keystroke is not a request (and not an AsyncStorage cache entry per keystroke).
function useDebounced<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return settled;
}

const styles = StyleSheet.create({
  body: {gap: spacing(4), paddingTop: spacing(2), paddingBottom: spacing(8), flexGrow: 1},
  group: {gap: spacing(2)},
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(3),
    paddingVertical: spacing(3),
    paddingHorizontal: spacing(3),
    borderWidth: 1,
    borderRadius: radii.md,
  },
  optionLabel: {flex: 1},
  // A real checkbox, not a tick glued to the front of the label: at a glance the
  // column of boxes is what tells the operator this is a multi-select at all.
  box: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  result: {gap: spacing(3)},
  picked: {gap: spacing(2)},
  pickedRow: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(2)},
  pickedChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(2),
    paddingVertical: spacing(1),
    paddingHorizontal: spacing(3),
    borderWidth: 1,
    borderRadius: radii.md,
  },
});

// Several targets can be picked at once, so this is a checkbox, not a radio:
// the role is what a screen reader announces, and "radio" would tell a blind
// operator that picking a second person unpicks the first.
function OptionRow({
  label,
  active,
  onPress,
}: {
  label: string;
  active: boolean;
  onPress: () => void;
}) {
  const p = useTheme();
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="checkbox"
      accessibilityState={{checked: active}}
      accessibilityLabel={label}
      style={({pressed}) => [
        styles.option,
        {
          backgroundColor: active ? p.accent : pressed ? p.muted : p.card,
          borderColor: active ? p.primary : p.border,
        },
      ]}>
      <View
        style={[
          styles.box,
          {
            backgroundColor: active ? p.primary : 'transparent',
            borderColor: active ? p.primary : p.input,
          },
        ]}>
        {active ? <Icon name="check" size={13} color={p.primaryForeground} /> : null}
      </View>
      <AppText
        variant="body"
        style={styles.optionLabel}
        color={active ? p.accentForeground : undefined}
        numberOfLines={1}>
        {label}
      </AppText>
    </Pressable>
  );
}

export function InviteForm({companyId, onDone}: {companyId: string; onDone: () => void}) {
  const {t} = useTranslation();
  const p = useTheme();
  const qc = useQueryClient();

  const [label, setLabel] = useState('');
  const [scopeType, setScopeType] = useState<TelegramScopeType>('company');
  const [scopeIds, setScopeIds] = useState<string[]>([]);
  const [pickedNames, setPickedNames] = useState<Record<string, string>>({});
  const [arrival, setArrival] = useState(true);
  const [departure, setDeparture] = useState(true);
  const [digest, setDigest] = useState(true);
  const [multiUse, setMultiUse] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search.trim(), SEARCH_DEBOUNCE_MS);
  const [minted, setMinted] = useState<CreateInviteResult | null>(null);

  const departments = useQuery({
    queryKey: ['control-departments', companyId],
    queryFn: () => requestControl<ControlDepartments>('/control/departments', companyId),
    enabled: scopeType === 'department',
  });

  const people = useQuery({
    queryKey: ['personnel-picker', companyId, debouncedSearch],
    queryFn: () => fetchPersonnelList(companyId, {search: debouncedSearch, perPage: 25}),
    enabled: scopeType === 'person',
  });

  const mint = useMutation({
    mutationFn: () =>
      createTelegramInvite(companyId, {
        label: label.trim(),
        scope_type: scopeType,
        scope_ids: scopeType === 'company' ? [] : scopeIds,
        notify_arrival: arrival,
        notify_departure: departure,
        include_in_digest: digest,
        multi_use: multiUse,
      }),
    onSuccess: result => {
      setMinted(result);
      qc.invalidateQueries({queryKey: ['telegram-recipients', companyId]});
    },
  });

  const pickScope = (next: TelegramScopeType) => {
    setScopeType(next);
    // Ids are not comparable across scope types — a department id left over
    // from before the switch would be sent as a personnel record_id.
    setScopeIds([]);
    setPickedNames({});
  };

  // Remember the name alongside the id: the person list is search-filtered, so
  // by the time the chips render, the row a name came from may be gone.
  const toggleScopeId = (id: string, name: string) => {
    setScopeIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
    setPickedNames(prev => ({...prev, [id]: name}));
  };

  // A department/person invite with nothing picked is rejected by sv_control;
  // don't let it be sent in the first place.
  const canSubmit = scopeType === 'company' || scopeIds.length > 0;

  if (minted) {
    return (
      <View style={styles.body}>
        <Card padding={4} style={[styles.result, {borderColor: p.primary, borderWidth: 1}]}>
          <AppText variant="heading">{label.trim() || t('telegram.newInvite')}</AppText>
          <AppText variant="mono" selectable>
            {minted.deep_link || t('telegram.codeOnly', {code: minted.code})}
          </AppText>
          <AppButton
            title={t('telegram.share')}
            icon="send"
            onPress={() => void shareInvite(t, {deepLink: minted.deep_link, code: minted.code})}
          />
        </Card>
        <AppButton title={t('actions.done')} tone="neutral" onPress={onDone} />
      </View>
    );
  }

  return (
    <View style={styles.body}>
      <FormCard>
      <AppInput
        label={t('telegram.labelField')}
        placeholder={t('telegram.labelPlaceholder')}
        value={label}
        onChangeText={setLabel}
      />

      <View style={styles.group}>
        <AppText variant="label">{t('telegram.scopeField')}</AppText>
        {/* Exactly one of three, and switching wipes the picked ids — a segmented
            track is the control that says "one of these", which three separate
            chips did not. */}
        <Segmented
          options={[
            {value: 'company' as TelegramScopeType, label: t('telegram.scopeCompany')},
            {value: 'department' as TelegramScopeType, label: t('telegram.scopeDepartment')},
            {value: 'person' as TelegramScopeType, label: t('telegram.scopePerson')},
          ]}
          value={scopeType}
          onChange={pickScope}
        />
      </View>

      {scopeType === 'department' ? (
        <View style={styles.group}>
          <AppText variant="label">{t('telegram.pickDepartments')}</AppText>
          {departments.isPending ? <Skeleton height={48} radius={radii.md} /> : null}
          {(departments.data?.departments ?? []).map(d => (
            <OptionRow
              key={d.department_id}
              label={d.name}
              active={scopeIds.includes(d.department_id)}
              onPress={() => toggleScopeId(d.department_id, d.name)}
            />
          ))}
          {scopeIds.length > 0 ? (
            <AppText variant="caption">
              {t('telegram.pickedCount', {count: scopeIds.length})}
            </AppText>
          ) : null}
        </View>
      ) : null}

      {scopeType === 'person' ? (
        <View style={styles.group}>
          <AppText variant="label">{t('telegram.pickPeople')}</AppText>
          <AppInput
            label={t('telegram.searchPerson')}
            value={search}
            onChangeText={setSearch}
            icon="search"
          />
          {people.isPending ? <Skeleton height={48} radius={radii.md} /> : null}
          {(people.data?.data ?? []).map(person => (
            <OptionRow
              key={person.record_id}
              label={person.full_name}
              active={scopeIds.includes(person.record_id)}
              onPress={() => toggleScopeId(person.record_id, person.full_name)}
            />
          ))}
          {/* The list is search-filtered, so people picked under an earlier query
              scroll out of view. Without this the operator cannot tell whether a
              selection survived, and re-taps a name they already chose. */}
          {scopeIds.length > 0 ? (
            <View style={styles.picked}>
              <AppText variant="caption">
                {t('telegram.pickedCount', {count: scopeIds.length})}
              </AppText>
              <View style={styles.pickedRow}>
                {scopeIds.map(id => (
                  <Pressable
                    key={id}
                    onPress={() => toggleScopeId(id, pickedNames[id] ?? id)}
                    accessibilityRole="button"
                    accessibilityLabel={t('telegram.unpick', {name: pickedNames[id] ?? id})}
                    style={({pressed}) => [
                      styles.pickedChip,
                      {
                        backgroundColor: p.accent,
                        borderColor: p.primary,
                        opacity: pressed ? 0.7 : 1,
                      },
                    ]}>
                    <AppText variant="label" color={p.accentForeground}>
                      {pickedNames[id] ?? id}
                    </AppText>
                    <Icon name="close" size={11} color={p.accentForeground} />
                  </Pressable>
                ))}
              </View>
            </View>
          ) : null}
        </View>
      ) : null}

      </FormCard>

      <FormCard title={t('telegram.whatField')}>
        <Toggle label={t('telegram.notifyArrival')} value={arrival} onChange={setArrival} />
        <Toggle label={t('telegram.notifyDeparture')} value={departure} onChange={setDeparture} />
        <Toggle label={t('telegram.includeInDigest')} value={digest} onChange={setDigest} />
        <Toggle
          label={t('telegram.multiUse')}
          hint={t('telegram.multiUseHint')}
          value={multiUse}
          onChange={setMultiUse}
        />
      </FormCard>

      <AppButton
        title={t('telegram.create')}
        size="lg"
        disabled={!canSubmit}
        loading={mint.isPending}
        onPress={() => mint.mutate()}
      />
      {mint.isError ? <Banner tone="error" title={t('telegram.saveFailed')} /> : null}
    </View>
  );
}
